import { Suspense, useEffect } from 'react';

import { TAccount } from '@helpers/Accounts/accounts.types';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getIsRCFOffer } from '@helpers/FormalOffers/getIsRCFOffer';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { Button, Text } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { H3, Skeleton } from '@ui';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './ConfirmForm.module.css';
import NextRepayment from './nextRepayment/NextRepayment';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { getCountry } from '../../../../iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import {
    SELECT_DRAW_DOWN_FUNDING_METHOD,
    SELECT_DRAW_DOWN_REPAYMENT_METHOD
} from '../../../drawDown/DrawDown.actions';
import {
    getOutstandingBalanceOnCurrentDay,
    getDrawDownAmount
} from '../../../drawDown/DrawDownSelectors';
import { SelectPaymentMethod } from '../../../earlyRepayment/confirmRepayment/SelectPaymentMethod';
import { TSelectedPayment } from '../../../earlyRepayment/EarlyRepayment.reducer';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { ViewConfirmFundingPaymentPlan } from '../../../myOffers/approvedView/RCFApprovedView/ViewConfirmFundingPaymentPlan';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import RBLConfirmFundingInfo from '../../../revenueBasedLoan/drawdown/ConfirmFundingInfo';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import PairItemContainer from '../../../ui/pairItemContainer/PairItemContainer';
import { CONFIRM_FUNDING } from '../../Funding.actions';

const mapStateToProps = (state: RootState) => {
    const outstandingBalanceOnCurrentDay =
        getOutstandingBalanceOnCurrentDay(state);
    const withdrawalAmount = getDrawDownAmount(state);

    return {
        drawdownState: state.drawDown,
        outstandingBalanceOnCurrentDay,
        withdrawalAmount
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    confirm: (amount: number, offerId: string, sweep: number) =>
        dispatch({
            type: CONFIRM_FUNDING,
            amount,
            offerId,
            sweep
        }),
    showSchedule: () => dispatch({ type: SHOW_REPAYMENT_SCHEDULE_MODAL }),
    selectDrawdownRepaymentMethod: (selectedRepayment?: TSelectedPayment) =>
        dispatch({
            type: SELECT_DRAW_DOWN_REPAYMENT_METHOD,
            selectedRepayment
        }),
    selectDrawdownFundingMethod: (selectedFunding?: TSelectedPayment) =>
        dispatch({
            type: SELECT_DRAW_DOWN_FUNDING_METHOD,
            selectedFunding
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type TProps = {
    drawdownAmount: number;
    isTopUp: boolean;
    offerId: string;
    selectedInputAmount: number;
    selectedSweepPercentage: number;
    formalOffer: TFormalOffer;
    outstandingAccount: TAccount | undefined;
};

// SelectPaymentMethod constructs and consumes a plain TSelectedPayment, so the
// form values can never carry the `bankDetails` field that lives on the redux
// `selectedFunding` state. `bankDetails` is read from `selectedFundingAccount`
// in the saga, never from these form values, so the form type stays honest here.
type TConfirmFormData = {
    payment_to: TSelectedPayment | null;
    payment_from: TSelectedPayment | null;
};

const Confirm = ({
    confirm,
    showSchedule,
    drawdownState,
    selectDrawdownFundingMethod,
    selectDrawdownRepaymentMethod,
    outstandingBalanceOnCurrentDay,
    drawdownAmount,
    selectedInputAmount,
    offerId,
    isTopUp,
    selectedSweepPercentage,
    formalOffer
}: TProps & PropsFromRedux) => {
    const { control, reset } = useForm<TConfirmFormData>({
        defaultValues: {
            payment_to: drawdownState.selectedFunding ?? null,
            payment_from: drawdownState.selectedRepayment ?? null
        }
    });

    // mergeStates deep-clones redux state on every dispatch, so
    // selectedFunding/selectedRepayment get a new object reference each time.
    // Keying the reset on the selection identity (`value`) keeps it from
    // re-firing on unrelated dispatches and clobbering the user's choice.
    useEffect(() => {
        reset({
            payment_to: drawdownState.selectedFunding ?? null,
            payment_from: drawdownState.selectedRepayment ?? null
        });
    }, [
        drawdownState.selectedFunding?.value,
        drawdownState.selectedRepayment?.value,
        reset
    ]);

    const totalOutstandingBalance =
        outstandingBalanceOnCurrentDay + drawdownAmount;

    return (
        <>
            <MainSectionWrapper className="pt-0">
                {selectedSweepPercentage ? (
                    <RBLConfirmFundingInfo
                        amount={selectedInputAmount}
                        sweep={selectedSweepPercentage}
                    />
                ) : (
                    <PairItemContainer>
                        <AmountSummary
                            selectedInputAmount={selectedInputAmount}
                            summaryTitle={<SummaryTitle isTopUp={isTopUp} />}
                            summaryBreakdown={
                                <SummaryBreakdown
                                    isTopUp={isTopUp}
                                    totalOutstandingBalance={
                                        totalOutstandingBalance
                                    }
                                />
                            }
                        />

                        <div className="box-border p-m min-l:p-2xl">
                            <NextRepayment />
                            <ErrorBoundary
                                fallback={(error) => (
                                    <FallbackErrorModal error={error} />
                                )}
                            >
                                <Suspense
                                    fallback={<Skeleton className="h-[20px]" />}
                                >
                                    <ShowNextRepaymentButton
                                        drawdownAmount={drawdownAmount}
                                        totalOutstandingBalanceAfterDrawdown={
                                            totalOutstandingBalance
                                        }
                                        formalOffer={formalOffer}
                                        showRepaymentSchedule={showSchedule}
                                    />
                                </Suspense>
                            </ErrorBoundary>
                        </div>
                    </PairItemContainer>
                )}

                <div className={styles.choosePayment}>
                    <div className={styles.ChoosePaymentTo}>
                        <form autoComplete="off">
                            <div className={styles.choosePaymentTo}>
                                <label>
                                    <FormattedMessage
                                        id="_.confirm_funding.paid_in_to"
                                        defaultMessage="Paid into"
                                    />
                                </label>
                                <Controller
                                    control={control}
                                    name="payment_to"
                                    render={({
                                        field: { onChange, value }
                                    }) => (
                                        <SelectPaymentMethod
                                            value={value}
                                            onChange={onChange}
                                            selectRepaymentMethod={(payment) =>
                                                selectDrawdownFundingMethod(
                                                    payment
                                                )
                                            }
                                            options={
                                                drawdownState.fundingPaymentOptions
                                            }
                                            data-testid="payment-to"
                                        />
                                    )}
                                />
                            </div>

                            <div className={styles.choosePaymentFrom}>
                                <div
                                    className={styles.choosePaymentFromContent}
                                >
                                    <label>
                                        <FormattedMessage
                                            id="_.confirm_funding.repaid_from"
                                            defaultMessage="Repaid from"
                                        />
                                    </label>

                                    {getCountry() === 'DE' ? (
                                        <div
                                            className={
                                                styles.choosePaymentFromDeText
                                            }
                                        >
                                            <FormattedMessage
                                                id="_.confirm_funding.repayment_from_explanation"
                                                defaultMessage="Repayment from explanation"
                                            />
                                        </div>
                                    ) : (
                                        <Controller
                                            control={control}
                                            name="payment_from"
                                            render={({
                                                field: { onChange, value }
                                            }) => (
                                                <SelectPaymentMethod
                                                    value={value}
                                                    onChange={onChange}
                                                    selectRepaymentMethod={(
                                                        payment
                                                    ) =>
                                                        selectDrawdownRepaymentMethod(
                                                            payment
                                                        )
                                                    }
                                                    options={
                                                        drawdownState.repaymentOptions
                                                    }
                                                    data-testid="payment-from"
                                                />
                                            )}
                                        />
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className={styles.newFundingConfirmButton}>
                        <div className="mx-auto max-w-[550px] text-center">
                            <Button
                                variant="success"
                                onClick={() =>
                                    confirm(
                                        drawdownAmount,
                                        offerId,
                                        selectedSweepPercentage
                                    )
                                }
                                size="large"
                                data-action-name="Confirm funding"
                            >
                                <FormattedMessage
                                    id="_.confirm_funding.confirm"
                                    defaultMessage="Confirm"
                                />
                            </Button>
                            <DEOnly>
                                <Text className="mt-s text-s">
                                    <FormattedMessage
                                        id="_.confirm_funding.voluntary_takeup_disclaimer"
                                        defaultMessage="By confirming, you declare that you have freely decided to proceed with the disbursement of the loan and had the opportunity to obtain offers from other lenders."
                                    />
                                </Text>
                            </DEOnly>
                        </div>
                    </div>
                </div>
            </MainSectionWrapper>
        </>
    );
};

const AmountSummary = ({
    selectedInputAmount,
    summaryBreakdown,
    summaryTitle
}: {
    selectedInputAmount: number;
    summaryBreakdown: React.ReactNode;
    summaryTitle: React.ReactNode;
}) => {
    return (
        <div
            className="box-border p-m min-l:p-2xl"
            data-testid="selected-amount"
        >
            <H3 className="mb-m text-m font-med min-l:mb-xl">{summaryTitle}</H3>
            <div className="mb-m min-l:mb-xl">
                <MoneyWithDecimals
                    className="text-2xl min-l:text-5xl min-l:leading-[48px]"
                    value={selectedInputAmount}
                    decimalWithSeparatorClassName="min-l:text-xl"
                />
            </div>
            <div>{summaryBreakdown}</div>
        </div>
    );
};

const SummaryTitle = ({ isTopUp }: { isTopUp: boolean }) => {
    if (isTopUp) {
        return (
            <FormattedMessage
                id="_.confirm_funding.top_up.summary_title"
                defaultMessage="You are requesting to withdraw"
            />
        );
    }
    return (
        <FormattedMessage
            id="_.confirm_funding.summary_title"
            defaultMessage="You are requesting to borrow"
        />
    );
};

const SummaryBreakdown = ({
    isTopUp,
    totalOutstandingBalance
}: {
    isTopUp: boolean;
    totalOutstandingBalance: number;
}) => {
    const formattedOutstandingBalance = (
        <span>
            <FormattedCurrency value={totalOutstandingBalance} />
        </span>
    );
    if (isTopUp) {
        return (
            <FormattedMessage
                id="_.confirm_funding.top_up.confirm_repayment.summary_breakdown"
                defaultMessage="This will increase your outstanding balance to {newBalance}."
                values={{
                    newBalance: formattedOutstandingBalance
                }}
            />
        );
    }

    return (
        <FormattedMessage
            id="_.confirm_funding.confirm_repayment.summary_breakdown"
            defaultMessage="This will set your outstanding balance to {newBalance}."
            values={{
                newBalance: formattedOutstandingBalance
            }}
        />
    );
};

const ShowNextRepaymentButton = ({
    totalOutstandingBalanceAfterDrawdown,
    drawdownAmount,
    formalOffer,
    showRepaymentSchedule
}: {
    totalOutstandingBalanceAfterDrawdown: number;
    drawdownAmount: number;
    formalOffer: TFormalOffer;
    showRepaymentSchedule: () => void;
}) => {
    if (getIsRCFOffer(formalOffer)) {
        const isFundedRCF = formalOffer.is_funded;

        return (
            <ViewConfirmFundingPaymentPlan
                offerId={formalOffer.offer_id!}
                drawdownAmount={drawdownAmount}
                balanceAfterDrawdown={totalOutstandingBalanceAfterDrawdown}
                isInRepaymentFreePeriod={isFundedRCF}
                buttonLabel={
                    isFundedRCF ? (
                        <FormattedMessage
                            id="_.outstanding_view.rcf_view_new_payment_plan"
                            defaultMessage="View new payment plan"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.rcf_approved_view.offer_details_view_payment_plan"
                            defaultMessage="View payment plan"
                        />
                    )
                }
            />
        );
    }

    return (
        <div>
            <span className={styles.link} onClick={showRepaymentSchedule}>
                <FormattedMessage
                    id="_.confirm_funding.repayment_breakdown"
                    defaultMessage="See full repayment schedule"
                />
            </span>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Confirm);
